import { useParams, Link, useNavigate } from "react-router-dom";
import { useCollections } from "../../hooks/useData";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import { formatMoneyInput, formatPLN } from "../../lib/finance";
import {
  COLLECTION_CONTRIBUTION_MODE,
  getCollectionContributionConfig,
  validateCollectionContributionConfig,
} from "../../lib/collections/contributionConfig";
import { buildCollectionUpdate } from "../../lib/collections/updateCollection";
import { useAuth } from "../../context/AuthContext";
import { useSchoolYear } from "../../context/SchoolYearContext";

function inputClass() {
  return "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-300";
}

export default function CollectionEditScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: collections = [], loading } = useCollections();
  const { isAdmin } = useAuth();
  const { selectedSchoolYearId, isSelectedYearActive, isSelectedYearDraft } = useSchoolYear();

  const collection = collections.find((c) => String(c.id) === String(id));
  const belongsToSelectedYear =
    collection && String(collection.school_year_id) === String(selectedSchoolYearId);
  const canWrite =
    isAdmin && (isSelectedYearActive || isSelectedYearDraft) && belongsToSelectedYear;

  const [name, setName] = useState("");
  const [target, setTarget] = useState(0);
  const [current, setCurrent] = useState(0);
  const [status, setStatus] = useState("aktywna");
  const [contributionMode, setContributionMode] = useState(COLLECTION_CONTRIBUTION_MODE.FIXED);
  const [isVoluntary, setIsVoluntary] = useState(false);
  const [minContribution, setMinContribution] = useState("");
  const [maxContribution, setMaxContribution] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [affectsClassBalance, setAffectsClassBalance] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (collection) {
      const contributionConfig = getCollectionContributionConfig(collection);
      setName(collection.name ?? "");
      setTarget(collection.target_amount ?? "");
      setCurrent(Number(collection.current_amount ?? 0));
      setStatus(collection.status ?? "aktywna");
      setContributionMode(contributionConfig.contributionMode);
      setIsVoluntary(contributionConfig.isVoluntary);
      setMinContribution(
        contributionConfig.minContribution === null
          ? ""
          : formatMoneyInput(contributionConfig.minContribution)
      );
      setMaxContribution(
        contributionConfig.maxContribution === null
          ? ""
          : formatMoneyInput(contributionConfig.maxContribution)
      );
      setStartDate(collection.start_date ?? "");
      setEndDate(collection.end_date ?? "");
      setAffectsClassBalance(collection.affects_class_balance !== false);
    }
  }, [collection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="rounded-[28px] border border-white/70 bg-white/80 px-6 py-8 text-slate-500 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
        Nie znaleziono zbiórki.
      </div>
    );
  }

  if (!canWrite) {
    return (
      <div className="space-y-4">
        <div className="rounded-[28px] border border-amber-200 bg-amber-50 px-6 py-5 text-sm font-medium text-amber-800 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
          Rok tylko do odczytu.
        </div>
        <Link
          to="/collection"
          className="inline-flex rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
        >
          Wróć do zbiórek
        </Link>
      </div>
    );
  }

  async function handleSave() {
    if (!name.trim()) {
      toast.error("Podaj nazwę zbiórki.");
      return;
    }

    if (
      contributionMode === COLLECTION_CONTRIBUTION_MODE.FIXED &&
      Number(target) <= 0
    ) {
      toast.error("Cel zbiórki musi być większy od 0.");
      return;
    }

    if (target !== "" && Number(target) <= 0) {
      toast.error("Łączny cel zbiórki musi być większy od 0 albo pozostać pusty.");
      return;
    }

    const contributionConfig = validateCollectionContributionConfig({
      contributionMode,
      isVoluntary,
      minContribution,
      maxContribution,
    });
    if (!contributionConfig.valid) {
      toast.error(
        contributionConfig.error === "MIN_BELOW_ONE_ZLOTY"
          ? "Minimalna wpłata musi wynosić co najmniej 1,00 zł."
          : contributionConfig.error === "MAX_BELOW_MIN"
            ? "Maksymalna wpłata nie może być niższa od minimalnej."
            : "Uzupełnij poprawny przedział wpłat."
      );
      return;
    }

    if (Number(current) < 0) {
      toast.error("Kwota zebrana nie może być ujemna.");
      return;
    }

    if (startDate && endDate && endDate < startDate) {
      toast.error("Data końcowa nie może być wcześniejsza od daty rozpoczęcia.");
      return;
    }

    if (!canWrite) {
      toast.error("Rok tylko do odczytu.");
      return;
    }

    try {
      setSaving(true);

      const { error } = await supabase
        .from("collections")
        .update(buildCollectionUpdate({
          name,
          targetAmount: target,
          currentAmount: current,
          status,
          contribution: contributionConfig,
          startDate,
          endDate,
          affectsClassBalance,
        }))
        .eq("id", id)
        .eq("school_year_id", selectedSchoolYearId);

      if (error) throw error;

      toast.success("Zbiórka została zapisana.");
      navigate("/collection");
    } catch (err) {
      toast.error(err.message || "Nie udało się zapisać zbiórki.");
    } finally {
      setSaving(false);
    }
  }

  function handleCloseCollection() {
    setStatus("zakończona");
  }

  function handleActivateCollection() {
    setStatus("aktywna");
  }

  const hasOverallTarget = Number(target) > 0;
  const progress =
    hasOverallTarget
      ? Math.min(100, (Number(current) / Number(target)) * 100)
      : 0;

  const missing = Math.max(0, Number(target) - Number(current));
  const goalReached = hasOverallTarget && Number(current) >= Number(target);
  const isFinished = status === "zakończona";

  return (
    <div className="space-y-6 lg:space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.82)_0%,rgba(236,253,245,0.72)_45%,rgba(255,247,237,0.76)_100%)] px-6 py-7 shadow-[0_18px_52px_rgba(15,23,42,0.06)]"
      >
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
            Finanse
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Edycja zbiórki
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            Zmień nazwę, kwoty i status zbiórki oraz sprawdź jej aktualny postęp.
          </p>
        </div>
      </motion.div>

      {isSelectedYearDraft && (
        <div className="rounded-[28px] border border-sky-200 bg-sky-50 px-6 py-5 text-sm font-medium text-sky-800 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
          Wersja robocza roku szkolnego. Dane mogą edytować wyłącznie administratorzy.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.45 }}
          className="rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm"
        >
          <div className="mb-6">
            <div className="text-xl font-semibold tracking-tight text-slate-900">
              Dane zbiórki
            </div>
            <div className="mt-1 text-sm text-slate-500">
              Zapisane zmiany będą widoczne od razu po odświeżeniu danych.
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Nazwa
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass()}
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={inputClass()}
              >
                <option value="aktywna">aktywna</option>
                <option value="zakończona">zakończona</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Łączny cel zbiórki (PLN){contributionMode === COLLECTION_CONTRIBUTION_MODE.FIXED ? " *" : ""}
              </label>
              <input
                type="number"
                step="0.01"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className={inputClass()}
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Wpłaty od
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className={inputClass()}
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Wpłaty do
              </label>
              <input
                type="date"
                min={startDate || undefined}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className={inputClass()}
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                <input
                  className="mt-1"
                  type="checkbox"
                  checked={affectsClassBalance}
                  onChange={(event) => setAffectsClassBalance(event.target.checked)}
                />
                <span>
                  <span className="font-medium">Uwzględniaj zebrane środki w saldzie klasy</span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">
                    Wyłącz dla zbiórek, których środki są przekazywane dalej i nie należą do budżetu klasy.
                  </span>
                </span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Sposób wpłaty
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="edit-contribution-mode"
                    checked={contributionMode === COLLECTION_CONTRIBUTION_MODE.FIXED}
                    onChange={() => setContributionMode(COLLECTION_CONTRIBUTION_MODE.FIXED)}
                  />
                  Stała kwota
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <input
                    type="radio"
                    name="edit-contribution-mode"
                    checked={contributionMode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE}
                    onChange={() => setContributionMode(COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE)}
                  />
                  Dowolna kwota w przedziale
                </label>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={isVoluntary}
                  onChange={(event) => setIsVoluntary(event.target.checked)}
                />
                Zbiórka dobrowolna
              </label>
            </div>

            {contributionMode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE && (
              <>
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                    Minimalna wpłata (PLN)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={minContribution}
                    onChange={(event) => setMinContribution(event.target.value)}
                    className={inputClass()}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                    Maksymalna wpłata (PLN)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={maxContribution}
                    onChange={(event) => setMaxContribution(event.target.value)}
                    className={inputClass()}
                  />
                </div>
              </>
            )}

            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                Zebrano (PLN)
              </label>
              <input
                type="number"
                step="0.01"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className={inputClass()}
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {!goalReached && status !== "zakończona" && (
              <button
                type="button"
                onClick={handleCloseCollection}
                disabled={saving}
                className="rounded-2xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
              >
                Oznacz jako zakończoną
              </button>
            )}

            {status === "zakończona" && (
              <button
                type="button"
                onClick={handleActivateCollection}
                disabled={saving}
                className="rounded-2xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-60"
              >
                Przywróć jako aktywną
              </button>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-transform duration-200 ${
                saving ? "opacity-60 cursor-not-allowed" : "hover:-translate-y-0.5"
              }`}
            >
              {saving ? "Zapisywanie..." : "Zapisz"}
            </button>

            <Link
              to="/collection"
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Wróć
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.45 }}
          className="space-y-6"
        >
          <div className="rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm">
            <div className="mb-5">
              <div className="text-xl font-semibold tracking-tight text-slate-900">
                Postęp zbiórki
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Aktualny stan realizacji celu.
              </div>
            </div>

            <div className="rounded-[24px] bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50 p-5">
              <div className="mb-3 flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-500">Zebrano</span>
                <span className="font-medium text-slate-800">
                  {formatPLN(current)}{hasOverallTarget ? ` / ${formatPLN(target)}` : ""}
                </span>
              </div>

              {contributionMode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE && (
                <div className="mb-3 text-sm font-medium text-emerald-800">
                  Dowolna wpłata: {formatPLN(minContribution)}–{formatPLN(maxContribution)}
                </div>
              )}

              {hasOverallTarget ? (
                <>
                  <div className="h-3 overflow-hidden rounded-full bg-white/80">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-amber-300"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>{progress.toFixed(1)}%</span>
                    <span>{missing > 0 ? `Brakuje ${formatPLN(missing)}` : "Cel osiągnięty"}</span>
                  </div>
                </>
              ) : (
                <div className="mt-2 text-sm font-medium text-emerald-700">
                  {isVoluntary ? "Zbiórka dobrowolna" : "Bez łącznego celu"}
                  {contributionMode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE &&
                    ` • Od ${formatPLN(minContribution)} do ${formatPLN(maxContribution)}`}
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  {hasOverallTarget ? "Cel" : contributionMode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE ? "Wpłata dobrowolna" : "Cel"}
                </div>
                <div className="mt-2 text-lg font-semibold text-slate-900">
                  {hasOverallTarget
                    ? formatPLN(target)
                    : contributionMode === COLLECTION_CONTRIBUTION_MODE.FLEXIBLE_RANGE
                      ? `${formatPLN(minContribution)}–${formatPLN(maxContribution)}`
                      : "Brak łącznego celu"}
                </div>
              </div>

              <div className="rounded-[22px] border border-slate-100 bg-slate-50/80 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Zebrano
                </div>
                <div className="mt-2 text-lg font-semibold text-emerald-700">
                  {formatPLN(current)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm">
            <div className="text-xl font-semibold tracking-tight text-slate-900">
              Status po zapisie
            </div>
            <div className="mt-3 rounded-[24px] border border-slate-100 bg-slate-50/80 p-4 text-sm leading-6 text-slate-600">
              Aktualnie ustawiony status:
              <span className="ml-2 font-semibold text-emerald-700">{status}</span>
              <p className="mt-2 text-sm text-slate-500">
                Zbiórka ze statusem „zakończona” nie będzie widoczna na dashboardzie jako aktywna,
                ale pozostanie w historii zbiórek.
              </p>
            </div>
          </div>

          {goalReached && !isFinished && (
            <div className="rounded-[30px] border border-emerald-200 bg-emerald-50/80 p-6 shadow-[0_12px_28px_rgba(16,185,129,0.08)]">
              <div className="text-base font-semibold text-emerald-800">
                Cel zbiórki został osiągnięty.
              </div>
              <p className="mt-2 text-sm leading-6 text-emerald-700/90">
                Możesz teraz oznaczyć tę zbiórkę jako zakończoną.
              </p>

              <div className="pt-4">
                <button
                  type="button"
                  onClick={handleCloseCollection}
                  disabled={saving}
                  className="rounded-2xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-60"
                >
                  Oznacz jako zakończoną
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
